import { AbstractDataServiceFeature } from 'plugins/tag/plugin_data_service/ts/Application/abstract/AbstractDataServiceFeature';
import { IDataServiceFeatureContext } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeatureContext';
import { IPTVChannelFallback } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IPTVChannelFallback';
import { IPTVResponse } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/IPTVResponse';
import { CONTENT_CONFIG, STREAM_TYPES } from 'plugins/tag/plugin_data_service/ts/Application/model/constants/PTVConstants';
import { PTVContentType } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/PTVContentType';
import { PTVMetadataResolver } from 'plugins/tag/plugin_data_service/ts/Application/utilities/PTVMetadataResolver';
import { PTVResponseBuilder } from 'plugins/tag/plugin_data_service/ts/Application/utilities/PTVResponseBuilder';
import { PTVUrlBuilder } from 'plugins/tag/plugin_data_service/ts/Application/utilities/PTVUrlBuilder';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { CONVEYOR_KEY_API_VIDEO_LOAD_PARAMS_VO } from 'tag/ts/tag/model/enums/ConveyorKeys';

/**
 * @copyright © Paramount Streaming
 * @description Data Service feature that enriches paramsVO with PTV metadata via the Conveyor pipeline so playback can start.
 * @see ../../../../../../../docs/plugins/tag/data-service/playback-params-builder.md
 */
export class PlaybackParamsBuilderFeature extends AbstractDataServiceFeature {
	static readonly #LOG_EXISTING_STITCHED_PATH = 'looking for existing stitchedPath:';
	static readonly #LOG_RESOLVED_STITCHED_PATH = 'resolved stitched path:';
	static readonly #LOG_RETRIEVED_STITCHED_PATH = 'retrieved stitchedPath:';
	static readonly #LOG_CREATED_ASSET_URL = 'created assetURL:';

	/**
	 * @description Class name exposed for logging and debugging, following the codebase-wide
	 * convention where every class surfaces its name (e.g. 'PlaybackParamsBuilderFeature').
	 * Not used for config lookup — see {@link featureKey} for that purpose.
	 */
	get name(): string {
		return 'PlaybackParamsBuilderFeature';
	}

	/**
	 * @description Concise feature identifier used for config lookup
	 * (config.features['PlaybackParamsBuilder']) and public API registration
	 * (tag.API.DATA.PlaybackParamsBuilder). Kept separate from {@link name} so the
	 * class name follows the codebase convention while the config/registry key
	 * remains a short, stable feature identifier.
	 */
	get featureKey(): string {
		return 'PlaybackParamsBuilder';
	}

	/**
	 * @description Installs the feature by registering a Conveyor transformer on the API.VIDEO.load > paramsVO pipeline.
	 * The transformer runs at priority 50, after paramsVO is created and before resource config is built.
	 * @param {IDataServiceFeatureContext} context
	 */
	protected doInstall( context: IDataServiceFeatureContext ): void {
		context.utils.Conveyor( CONVEYOR_KEY_API_VIDEO_LOAD_PARAMS_VO )
			.transformer( this.#transformer, { priority: 50 } );
	}

	/**
	 * @description Determines if paramsVO should be enriched with PTV data
	 * @param {IParamsVO} paramsVO
	 * @returns {boolean}
	 * @private
	 */
	#shouldEnrich = ( paramsVO: IParamsVO ): boolean => {
		return paramsVO?.CONTENT?.partner === CONTENT_CONFIG.partner;
	};

	/**
	 * @description Transformer function registered on the Conveyor pipeline.
	 * Enriches paramsVO with PTV metadata when the request is for a supported partner.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<IParamsVO>}
	 * @private
	 */
	#transformer = async ( paramsVO: IParamsVO ): Promise<IParamsVO> => {
		if ( !this.#shouldEnrich( paramsVO ) ) {
			return paramsVO;
		}

		const ptvParams = await this.#fetchPTVData( paramsVO );
		const enrichedParams = await this.#enrichParamsWithPTVData( paramsVO, ptvParams );
		this.logger?.log( '[transformer]', 'enriched paramsVO:', enrichedParams );

		return enrichedParams;
	};

	/**
	 * @description Enriches paramsVO with PTV API data by deep merging fetched data into paramsVO.
	 * Adds a META.enriched flag to indicate this paramsVO has been processed by the data service.
	 * @param {IParamsVO} paramsVO - Smart Tag paramsVO object
	 * @param {IPTVResponse | IPTVChannelFallback} ptvData - PTV data fetched from Pluto APIs
	 * @returns {Promise<IParamsVO>} The enriched paramsVO with additional PTV metadata
	 * @private
	 */
	#enrichParamsWithPTVData = async ( paramsVO: IParamsVO, ptvData: IPTVResponse | IPTVChannelFallback ): Promise<IParamsVO> => {
		this.logger?.log( '[enrichParamsWithPTVData]:', 'paramsVO:', paramsVO, 'fetched PTV data:', ptvData );

		// Resource metadata persists across loads. Clear the prior scalar before merging so a VOD
		// response without a source row cannot retain the previous resource's categorySlug.
		if ( paramsVO?.CONTENT?.resource?.metadata ) {
			delete paramsVO.CONTENT.resource.metadata.categorySlug;
		}

		const enrichment = {
			META: {
				enriched: true
			},
			...ptvData
		};

		this.logger?.log( '[enrichParamsWithPTVData]:', 'deep merge enrichment into paramsVO...', 'enrichment:', enrichment, 'paramsVO:', paramsVO );

		return this.context?.utils.DeepMerge.deepmerge( paramsVO, enrichment );
	};

	/**
	 * @description Fetches PTV data required for playback based on content type.
	 * If metadata is already present in paramsVO, retrieves data from there.
	 * Otherwise makes fetch calls to PTV APIs.
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<IPTVResponse | IPTVChannelFallback>}
	 * @throws {Error} When required STITCHER parameters are missing
	 * @private
	 */
	#fetchPTVData = async ( paramsVO: IParamsVO ): Promise<IPTVResponse | IPTVChannelFallback> => {
		this.logger?.log( 'fetchPTVData', paramsVO );

		if (
			!paramsVO?.STITCHER?.id ||
			!paramsVO?.STITCHER?.format ||
			!paramsVO?.STITCHER?.type ||
			!paramsVO?.STITCHER?.token
		) {
			throw new Error( 'paramsVO.STITCHER missing required parameters' );
		}

		switch ( paramsVO.STITCHER.type ) {
			case PTVContentType.CHANNEL:
				return this.#fetchPTVChannel( paramsVO );
			case PTVContentType.MOVIE:
				return this.#fetchPTVMovie( paramsVO );
			case PTVContentType.EPISODE:
				return this.#fetchPTVEpisode( paramsVO );
			default:
				throw new Error( 'unknown stitcher content type' );
		}
	};

	/**
	 * @description Fetches stitcher parameters required to play PTV channel content.
	 * If paramsVO includes channels metadata, stitched path will be retrieved from there,
	 * otherwise it will be fetched from the channels API.
	 *
	 * `window.stitcherPaths` is checked before the API call to support future live DASH
	 * (PTVWS-3446 Phase 2) without requiring an additional code change. Currently a no-op
	 * since TN Web only populates `window.stitcherPaths` for VOD content.
	 *
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<IPTVResponse | IPTVChannelFallback>}
	 * @private
	 */
	#fetchPTVChannel = async ( paramsVO: IParamsVO ): Promise<IPTVResponse | IPTVChannelFallback> => {
		this.logger?.log( 'fetchPTVChannel', paramsVO );

		let stitchedPath = PTVMetadataResolver.resolveChannelStitchedPath( paramsVO );
		this.logger?.log( 'fetchPTVChannel', PlaybackParamsBuilderFeature.#LOG_EXISTING_STITCHED_PATH, stitchedPath );

		if ( !stitchedPath ) {
			// Phase 2 (PTVWS-3446): check window.stitcherPaths before falling through to the API call.
			// Currently a no-op for live — TN Web populates this for VOD only; included for future live DASH support.
			stitchedPath = PTVMetadataResolver.resolveStitchedPathFromWindow( paramsVO );
		}

		if ( !stitchedPath ) {
			const apiService = this.getApiService();
			const responseData = await apiService.fetchData( paramsVO );
			this.logger?.log( 'fetchPTVChannel', 'fetched channel metadata response:', responseData as object );

			const idField = apiService.getAdapterId() === 'pluto' ? 'slug' : 'id';
			stitchedPath = PTVMetadataResolver.resolveStitchedPathFromApiResponse(
				responseData,
				paramsVO,
				idField
			);
			this.logger?.log( 'fetchPTVChannel', PlaybackParamsBuilderFeature.#LOG_RESOLVED_STITCHED_PATH, stitchedPath );
		}

		if ( !stitchedPath ) {
			// PLBCK-2911: Return partial enrichment with isLive=true so player treats channel as live (DVR/seek behavior).
			return {
				CONTENT: {
					isLive: true
				},
				STITCHER: {
					streamType: STREAM_TYPES.LIVE
				}
			};
		}

		this.logger?.log( 'fetchPTVChannel', PlaybackParamsBuilderFeature.#LOG_RETRIEVED_STITCHED_PATH, stitchedPath );
		const assetURL = PTVUrlBuilder.createAssetURL( paramsVO, stitchedPath );
		this.logger?.log( 'fetchPTVChannel', PlaybackParamsBuilderFeature.#LOG_CREATED_ASSET_URL, assetURL );

		return PTVResponseBuilder.buildChannelResponse( paramsVO, assetURL );
	};

	/**
	 * @description Fetches stitcher parameters required to play PTV movie content.
	 *
	 * Resolution order:
	 * 1. If `STITCHER.items` is present, resolves stitched path from the matching item.
	 * 2. If no path found and `accessToken` is present (CTV — always generates it), fetches path from the API.
	 * 3. If no path found and `accessToken` is absent (TN Web — never supplies it):
	 *    a. Check `window.stitcherPaths[id]` (PTVWS-3446 Phase 2) — selects DASH when Widevine-capable, HLS otherwise.
	 *    b. Fall back to synthesis from `STITCHER.id` (Phase 1) when `window.stitcherPaths` is absent.
	 *
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<IPTVResponse>}
	 * @private
	 */
	#fetchPTVMovie = async ( paramsVO: IParamsVO ): Promise<IPTVResponse> => {
		this.logger?.log( 'fetchPTVMovie', paramsVO );

		let stitchedPath = PTVMetadataResolver.resolveItemStitchedPath( paramsVO );
		this.logger?.log( 'fetchPTVMovie', PlaybackParamsBuilderFeature.#LOG_EXISTING_STITCHED_PATH, stitchedPath );

		if ( !stitchedPath ) {
			if ( paramsVO?.STITCHER?.accessToken ) {
				// CTV app always generates and supplies accessToken — use API call flow.
				const responseData = await this.getApiService().fetchData( paramsVO );
				this.logger?.log( 'fetchPTVMovie', 'fetched metadata responseData:', responseData as object );

				stitchedPath = PTVMetadataResolver.resolveStitchedPathFromApiResponse(
					{ data: responseData },
					paramsVO,
					'_id'
				);
				this.logger?.log( 'fetchPTVMovie', PlaybackParamsBuilderFeature.#LOG_RESOLVED_STITCHED_PATH, stitchedPath );
			} else {
				// Web clients never supply accessToken.
				// Phase 2 (PTVWS-3446): prefer path from window.stitcherPaths set by TN Web after a
				// server-side /v4.0/streaming-url.json call; selects DASH when Widevine-capable, HLS otherwise.
				// Phase 1 fallback: synthesize from STITCHER.id when window.stitcherPaths is absent.
				stitchedPath = PTVMetadataResolver.resolveStitchedPathFromWindow( paramsVO )
					?? PTVMetadataResolver.synthesizePathFromId( paramsVO, 'episode' );
				this.logger?.log( 'fetchPTVMovie', PlaybackParamsBuilderFeature.#LOG_RESOLVED_STITCHED_PATH, stitchedPath );
			}
		}

		if ( !stitchedPath ) {
			throw new Error( `no stitched path found for id: ${paramsVO.STITCHER.id}` );
		}

		this.logger?.log( 'fetchPTVMovie', PlaybackParamsBuilderFeature.#LOG_RETRIEVED_STITCHED_PATH, stitchedPath );
		const assetURL = PTVUrlBuilder.createAssetURL( paramsVO, stitchedPath );
		this.logger?.log( 'fetchPTVMovie', PlaybackParamsBuilderFeature.#LOG_CREATED_ASSET_URL, assetURL );

		return PTVResponseBuilder.buildVodResponse( paramsVO, assetURL );
	};

	/**
	 * @description Fetches stitcher parameters required to play PTV VOD episode content.
	 *
	 * Resolution order:
	 * 1. If `STITCHER.episodes` is present, resolves stitched path from the matching episode.
	 * 2. If no path found and `accessToken` is present (CTV — always generates it), fetches path from the API.
	 * 3. If no path found and `accessToken` is absent (TN Web — never supplies it):
	 *    a. Check `window.stitcherPaths[id]` (PTVWS-3446 Phase 2) — selects DASH when Widevine-capable, HLS otherwise.
	 *    b. Fall back to synthesis from `STITCHER.id` (Phase 1) when `window.stitcherPaths` is absent.
	 *
	 * @param {IParamsVO} paramsVO
	 * @returns {Promise<IPTVResponse>}
	 * @private
	 */
	#fetchPTVEpisode = async ( paramsVO: IParamsVO ): Promise<IPTVResponse> => {
		this.logger?.log( 'fetchPTVEpisode', paramsVO );

		let stitchedPath = PTVMetadataResolver.resolveEpisodeStitchedPath( paramsVO );
		this.logger?.log( 'fetchPTVEpisode', PlaybackParamsBuilderFeature.#LOG_EXISTING_STITCHED_PATH, stitchedPath );

		if ( !stitchedPath ) {
			if ( paramsVO?.STITCHER?.accessToken ) {
				// CTV app always generates and supplies accessToken — use API call flow.
				const responseData = await this.getApiService().fetchData( paramsVO );
				this.logger?.log( 'fetchPTVEpisode', 'fetched episode metadata response:', responseData as object );

				stitchedPath = PTVMetadataResolver.resolveStitchedPathFromApiResponse(
					{ data: responseData },
					paramsVO,
					'_id'
				);
				this.logger?.log( 'fetchPTVEpisode', PlaybackParamsBuilderFeature.#LOG_RESOLVED_STITCHED_PATH, stitchedPath );
			} else {
				// Web clients never supply accessToken.
				// Phase 2 (PTVWS-3446): prefer path from window.stitcherPaths set by TN Web after a
				// server-side /v4.0/streaming-url.json call; selects DASH when Widevine-capable, HLS otherwise.
				// Phase 1 fallback: synthesize from STITCHER.id when window.stitcherPaths is absent.
				stitchedPath = PTVMetadataResolver.resolveStitchedPathFromWindow( paramsVO )
					?? PTVMetadataResolver.synthesizePathFromId( paramsVO, 'episode' );
				this.logger?.log( 'fetchPTVEpisode', PlaybackParamsBuilderFeature.#LOG_RESOLVED_STITCHED_PATH, stitchedPath );
			}
		}

		if ( !stitchedPath ) {
			throw new Error( `no stitched path found for id: ${paramsVO.STITCHER.id}` );
		}

		this.logger?.log( 'fetchPTVEpisode', PlaybackParamsBuilderFeature.#LOG_RETRIEVED_STITCHED_PATH, stitchedPath );
		const assetURL = PTVUrlBuilder.createAssetURL( paramsVO, stitchedPath );
		this.logger?.log( 'fetchPTVEpisode', PlaybackParamsBuilderFeature.#LOG_CREATED_ASSET_URL, assetURL );

		return PTVResponseBuilder.buildVodResponse( paramsVO, assetURL );
	};
}